'use client'

import * as React from 'react'

import type { AssetProp } from '@web/components/prod/details/util'
import { getAssetsDownloadOptions } from '@web/components/prod/details/util'

/**
 * This context is meant to be used in client components that need product details; Its purpose is to purely avoid prop drilling.
 * Server componets should receive data via props.
 * */
interface ProductDetailsContextValue {
  wasDownloaded: boolean
  setWasDownloaded: React.Dispatch<React.SetStateAction<boolean>>
  paymentUrl: string
  setPaymentUrl: React.Dispatch<React.SetStateAction<string>>
  selectedAsset: {
    assetId: string
    assetPath: string
  }
  setSelectedAsset: React.Dispatch<
    React.SetStateAction<{
      assetId: string
      assetPath: string
    }>
  >
}

const ProductDetailsContext = React.createContext<ProductDetailsContextValue>(null!)

type ContextProviderProps = {
  categoryId: string
  assets: AssetProp[] | undefined
} & React.PropsWithChildren

export function SelectedAssetProvider({ children, categoryId, assets }: ContextProviderProps) {
  const downloadOptions = getAssetsDownloadOptions(categoryId, assets)
  const defaultSelectedAsset = (downloadOptions
    .filter(asset => asset.downloadable || asset.filePath.endsWith('.pdf'))
    .toSorted((asset1, asset2) =>
      (asset1.resolutionWidth ?? 0) > (asset2.resolutionWidth ?? 0) ? -1 : 1,
    ) || [])?.[0]

  React.useEffect(() => {
    setSelectedAsset({
      assetId: defaultSelectedAsset?.id ?? '',
      assetPath: defaultSelectedAsset?.filePath ?? '',
    })
  }, [defaultSelectedAsset])

  const [selectedAsset, setSelectedAsset] = React.useState<{ assetId: string; assetPath: string }>({
    assetId: defaultSelectedAsset?.id ?? '',
    assetPath: defaultSelectedAsset?.filePath ?? '',
  })
  const [wasDownloaded, setWasDownloaded] = React.useState(false)
  const [paymentUrl, setPaymentUrl] = React.useState('')

  return (
    <ProductDetailsContext.Provider
      value={{
        selectedAsset,
        setSelectedAsset,
        wasDownloaded,
        setWasDownloaded,
        paymentUrl,
        setPaymentUrl,
      }}
    >
      {children}
    </ProductDetailsContext.Provider>
  )
}

export function useSelectedAssetState() {
  const context = React.useContext(ProductDetailsContext)
  if (!context) {
    throw new Error('`useSelectedAssetState()` must be used within a `<SelectedAssetProvider />')
  }
  return [context.selectedAsset, context.setSelectedAsset] as const
}

export function useWasDownloadedState() {
  const context = React.useContext(ProductDetailsContext)
  if (!context) {
    throw new Error('`useWasDownloadedState()` must be used within a `<SelectedAssetProvider />')
  }
  return [context.wasDownloaded, context.setWasDownloaded] as const
}

export function useNeedsPaymentState() {
  const context = React.useContext(ProductDetailsContext)
  if (!context) {
    throw new Error('`useNeedsPaymentState()` must be used within a `<SelectedAssetProvider />')
  }
  return [context.paymentUrl, context.setPaymentUrl] as const
}
